<template>
  <div class="coupon_picker_popup" @click.self="close">
    <div
      class="coupon_picker_sheet"
      :inert="!!detailCouponUuid"
      :aria-hidden="detailCouponUuid ? 'true' : null"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coupon_picker_title"
    >
      <div class="row_center_between coupon_picker_head">
        <div>
          <div id="coupon_picker_title" class="coupon_picker_title">
            選擇優惠券
          </div>
          <div class="coupon_picker_sub">每筆訂單限用一張</div>
        </div>
        <button
          type="button"
          class="coupon_picker_close"
          aria-label="關閉"
          @click="close"
        >
          ×
        </button>
      </div>
      <div class="coupon_picker_list">
        <couponLoading v-if="loading" text="正在載入優惠券…" />
        <div class="coupon_picker_state" v-else-if="loadError">
          優惠券載入失敗，請稍後再試
          <button type="button" class="coupon_picker_retry" @click="load">
            重新載入
          </button>
        </div>
        <div class="coupon_picker_state" v-else-if="!getCouponCards.length">
          目前沒有可使用的優惠券
        </div>
        <template v-else>
          <div
            v-for="card in getCouponCards"
            :key="card.memberCouponUuid"
            class="coupon_picker_card"
            :class="{ selected: card.isSelected, disabled: !card.isSelectable }"
          >
            <button
              type="button"
              class="coupon_picker_select"
              :disabled="!card.isSelectable || !!validatingUuid"
              :aria-pressed="card.isSelected"
              @click="selectCoupon(card)"
            >
              <span class="coupon_picker_radio"></span>
              <span class="coupon_picker_body">
                <span class="coupon_picker_card_title">{{ card.title }}</span>
                <span
                  class="coupon_picker_card_sub coupon_picker_exchange"
                  v-if="card.exchangeText"
                  >{{ card.exchangeText }}</span
                >
                <span class="coupon_picker_card_sub" v-if="card.subText">{{
                  card.subText
                }}</span>
              </span>
            </button>
            <div class="coupon_picker_actions">
              <span class="coupon_picker_right">
                <couponLoading
                  v-if="validatingUuid === card.memberCouponUuid"
                  is-inline
                  text="確認中…"
                />
                <template v-else>{{ card.rightText }}</template>
              </span>
              <button
                type="button"
                class="coupon_picker_detail"
                :disabled="!!validatingUuid"
                :aria-label="`查看${card.title}詳情`"
                @click="showDetail(card, $event)"
              >
                詳情
              </button>
            </div>
          </div>
        </template>
      </div>
      <div class="row_center_between coupon_picker_foot">
        <button type="button" class="coupon_picker_none" @click="clearCoupon">
          不使用優惠券
        </button>
        <button type="button" class="coupon_picker_done" @click="close">
          關閉
        </button>
      </div>
    </div>
    <CouponPickerDetailPopup
      v-if="detailCouponUuid"
      :key="detailCouponUuid"
      :memberCouponUuid="detailCouponUuid"
      :fallbackTitle="detailCouponTitle"
      @close="closeDetail"
    />
  </div>
</template>
<script>
import CouponPickerDetailPopup from "@/components/coupon/couponPickerDetailPopup.vue";
import couponLoading from "@/components/coupon/couponLoading.vue";
import {
  listMemberCouponsV2,
  validateOrderCouponV2,
} from "@/services/api.service";
import {
  getTimeSlotReferencePoint,
  getVisibleCategoryIds,
  isCategoryInVisibleSet,
  LINE_CHANNEL_FIELD,
} from "@/utils/categoryTimeSlot.utils";
import commonUtilities from "@/utils/common.utils";
import { getCouponCalculation } from "@/utils/couponCalculation.utils";
import {
  getCouponNormalizedOrder,
  getCouponUnavailableReasonTexts,
  getSelectedCouponFromValidation,
} from "@/utils/couponCheckout.utils";
import {
  COUPON_DATE_FORMAT,
  formatCouponDateTime,
  getCouponCheckoutErrorMessage,
  getCouponValidFromText,
  getCouponValidFromAt,
  getCouponValidUntilAt,
  isCouponExpired,
  isCouponNotYetValid,
} from "@/utils/coupon.utils";
import {
  buildCouponExchangeOrderItem,
  getCouponExchangeItemNames,
  getCouponExchangePreviewItem,
  getCouponExchangeTarget,
  isCouponExchangeItem,
  removeCouponExchangeItems,
} from "@/utils/couponExchange.utils";
import { getFrontEndName } from "@/utils/productName.utils";

const PICKER_PAGE_SIZE = 50;
const MAX_TIMER_DELAY = 2147483647;
const UNMET_CONDITION_CODES = [
  "MIN_ORDER_AMOUNT_NOT_MET",
  "REQUIRED_TARGET_QUANTITY_NOT_MET",
  "BENEFIT_TARGET_NOT_IN_ORDER",
  "ORDER_ITEMS_EMPTY",
];

export default {
  components: { CouponPickerDetailPopup, couponLoading },
  props: {
    // 點餐方式（路由 method），判斷兌換商品所在分類的販售時段
    orderMethod: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "exchange"],
  data() {
    return {
      loading: false,
      loadError: false,
      items: [],
      // validate 回傳的最新規則，覆蓋清單摘要重新試算
      latestRules: {},
      validatingUuid: "",
      detailCouponUuid: "",
      detailCouponTitle: "",
      detailTrigger: null,
      nowMs: Date.now(),
      validityTimer: null,
      isPickerActive: true,
    };
  },
  computed: {
    getCart() {
      return this.$store.state.cart.cart;
    },
    getStoreMemberUuid() {
      return this.$store.getters["userData/storeMemberUuid"];
    },
    getSelectedUuid() {
      return this.$store.getters["cart/couponApplication"].status === "applied"
        ? this.$store.state.cart.selectedCoupon.memberCouponUuid
        : "";
    },
    getCouponCards() {
      const cards = this.items.map((item) => this.getCouponCard(item, this.nowMs));
      // 可用券優先；未達訂單條件的券保留在後方，供會員查看原因。
      return cards
        .map((card, index) => ({ card, index }))
        .filter(({ card }) => card.isSelectable || card.hasUnmetConditions)
        .sort(
          (a, b) =>
            Number(b.card.isSelectable) - Number(a.card.isSelectable) ||
            b.card.discountAmount - a.card.discountAmount ||
            a.index - b.index
        )
        .map(({ card }) => card);
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.isPickerActive = false;
    clearTimeout(this.validityTimer);
  },
  methods: {
    showDetail(card, event) {
      if (
        (!card.isSelectable && !card.hasUnmetConditions) ||
        this.validatingUuid
      )
        return;
      this.detailTrigger = event?.currentTarget || null;
      this.detailCouponTitle = card.title;
      this.detailCouponUuid = card.memberCouponUuid;
    },
    closeDetail() {
      const trigger = this.detailTrigger;
      this.detailCouponUuid = "";
      this.detailCouponTitle = "";
      this.detailTrigger = null;
      this.$nextTick(() => {
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      });
    },
    async load() {
      clearTimeout(this.validityTimer);
      this.loading = true;
      this.loadError = false;
      try {
        const data = await listMemberCouponsV2({
          page: 1,
          pageSize: PICKER_PAGE_SIZE,
          status: "available",
        });
        if (!Array.isArray(data?.items))
          throw new Error("優惠券清單資料不完整");
        this.items = data.items;
        this.latestRules = {};
      } catch (error) {
        this.loadError = true;
      } finally {
        this.loading = false;
        this.nowMs = Date.now();
        this.scheduleValidityUpdate();
      }
    },
    scheduleValidityUpdate() {
      clearTimeout(this.validityTimer);
      if (!this.isPickerActive) return;
      const upcoming = this.items
        .flatMap((item) => [getCouponValidFromAt(item), getCouponValidUntilAt(item)])
        .map((value) => Date.parse(value))
        .filter((ms) => Number.isFinite(ms) && ms > this.nowMs);
      if (!upcoming.length) return;
      const nextBoundary = Math.min(...upcoming);
      this.validityTimer = setTimeout(() => {
        if (!this.isPickerActive) return;
        this.nowMs = Date.now();
        if (this.nowMs >= nextBoundary) {
          this.load();
        } else {
          this.scheduleValidityUpdate();
        }
      }, Math.min(nextBoundary - this.nowMs, MAX_TIMER_DELAY));
    },
    getCouponCard(item, nowMs) {
      const card = {
        memberCouponUuid: item.member_coupon_uuid,
        title: item.title || item.public_summary?.title || "優惠券",
        isSelected: item.member_coupon_uuid === this.getSelectedUuid,
        isSelectable: false,
        hasUnmetConditions: false,
        discountAmount: 0,
        rightText: "無法使用",
        subText: "",
      };
      if (
        (item.status && item.status !== "available") ||
        (item.effective_status && item.effective_status !== "available") ||
        (item.template_status && item.template_status !== "active") ||
        item.unavailable_reason
      ) {
        return { ...card, subText: "優惠活動已停用" };
      }
      if (isCouponExpired(item, nowMs)) {
        return { ...card, subText: "優惠券已到期" };
      }
      const validFromText = getCouponValidFromText(item, nowMs);
      if (validFromText) {
        return { ...card, rightText: "尚未生效", subText: validFromText };
      }
      const rules =
        this.latestRules[item.member_coupon_uuid] || item.public_summary;
      const { cart, exchange } = this.getCouponCheckoutCart(
        item.member_coupon_uuid,
        rules
      );
      let calculation;
      try {
        calculation = getCouponCalculation({
          currentPublicRules: rules,
          normalizedOrder: getCouponNormalizedOrder({ cart }),
        });
      } catch (error) {
        return { ...card, subText: "此訂單暫不支援使用此優惠券" };
      }
      // 兌換商品無法加入：其他使用條件未達時先顯示未達原因
      const blockedText = this.getExchangeBlockedText(exchange);
      const unmetConditions = calculation.isApplicable
        ? []
        : blockedText
        ? calculation.unmetConditions.filter(
            (code) => code !== "BENEFIT_TARGET_NOT_IN_ORDER"
          )
        : calculation.unmetConditions;
      if (blockedText && !unmetConditions.length) {
        return {
          ...card,
          hasUnmetConditions: true,
          subText: blockedText,
        };
      }
      if (!calculation.isApplicable) {
        return {
          ...card,
          hasUnmetConditions: unmetConditions.some((code) =>
            UNMET_CONDITION_CODES.includes(code)
          ),
          rightText: "未達條件",
          subText: getCouponUnavailableReasonTexts({
            currentPublicRules: rules,
            unmetConditions,
            isApplicable: false,
          }).join("、"),
        };
      }
      const validUntil = formatCouponDateTime(
        getCouponValidUntilAt(item),
        item.timezone_name,
        COUPON_DATE_FORMAT
      );
      return {
        ...card,
        isSelectable: true,
        discountAmount: calculation.couponDiscountAmount,
        rightText: `可折 $${calculation.couponDiscountAmount}`,
        exchangeText: this.getExchangeText(exchange),
        subText: validUntil ? `使用期限至 ${validUntil}` : "",
      };
    },
    // 試算用購物車：已套用的券用目前購物車；其他券先移除兌換行，需加兌換商品時再接一行試算行
    getCouponCheckoutCart(memberCouponUuid, rules) {
      if (memberCouponUuid === this.getSelectedUuid) {
        return { cart: this.getCart, exchange: { status: "not_exchange" } };
      }
      // 改選其他券時目前券的兌換商品會被移除，以移除後的購物車試算
      const baseCart = removeCouponExchangeItems(this.getCart, null);
      const exchange = this.getCouponExchange(rules, baseCart);
      if (exchange.status !== "ready") {
        return { cart: baseCart, exchange };
      }
      const previewItem = getCouponExchangePreviewItem({
        menuItem: exchange.menuItem,
        quantity: exchange.quantity,
        memberCouponUuid,
      });
      return { cart: [...baseCart, previewItem], exchange };
    },
    getCouponExchange(rules, cart) {
      const { state, getters } = this.$store;
      const menuTimeSettings = getters["spreadsheet/menuTimeSettings"];
      const referencePoint = getTimeSlotReferencePoint({
        method: this.orderMethod,
        pickupDate: state.cart.payment?.pickupDate,
        pickupTime: state.cart.payment?.pickupTime,
      });
      const visibleIds = getVisibleCategoryIds(
        menuTimeSettings,
        LINE_CHANNEL_FIELD,
        referencePoint
      );
      return getCouponExchangeTarget({
        rules,
        cart,
        menuGroups: getters["spreadsheet/productsInCategory"] || [],
        isCategoryVisible: (categoryId) =>
          isCategoryInVisibleSet(visibleIds, categoryId),
        useDbData: !!state.spreadsheet?.useDbData,
        specifications: getters["spreadsheet/specifications"] || [],
      });
    },
    getExchangeBlockedText(exchange) {
      if (exchange.status === "out_of_stock")
        return `兌換商品庫存不足，無法兌換 ${exchange.quantity} 份`;
      if (exchange.status === "unavailable") return "兌換商品目前無法點購";
      return "";
    },
    getExchangeText(exchange) {
      if (!["ready", "use_existing"].includes(exchange.status)) return "";
      const name = getFrontEndName(exchange.menuItem.name);
      if (exchange.status === "use_existing")
        return `兌換購物車中的「${name}」×${exchange.quantity}`;
      return exchange.needsDetail
        ? `選用後選擇「${name}」規格並加入`
        : `選用後自動加入「${name}」×${exchange.quantity}`;
    },
    async selectCoupon(card) {
      if (!card.isSelectable || this.validatingUuid) return;
      const item = this.items.find((item) => item.member_coupon_uuid === card.memberCouponUuid);
      if (!item || isCouponExpired(item, Date.now()) || isCouponNotYetValid(item, Date.now())) {
        this.nowMs = Date.now();
        commonUtilities.createToast("error", "此優惠券目前無法使用，請重新選擇");
        this.load();
        return;
      }
      this.validatingUuid = card.memberCouponUuid;
      try {
        const data = await validateOrderCouponV2({
          memberCouponUuid: card.memberCouponUuid,
        });
        const selectedCoupon = getSelectedCouponFromValidation({
          data,
          memberCouponUuid: card.memberCouponUuid,
          storeMemberUuid: this.getStoreMemberUuid,
          fallbackTitle: card.title,
        });
        const rules = selectedCoupon.currentPublicRules;
        const { cart, exchange } = this.getCouponCheckoutCart(
          card.memberCouponUuid,
          rules
        );
        const blockedText = this.getExchangeBlockedText(exchange);
        const calculation = getCouponCalculation({
          currentPublicRules: rules,
          normalizedOrder: getCouponNormalizedOrder({ cart }),
        });
        if (blockedText || !calculation.isApplicable) {
          this.latestRules = {
            ...this.latestRules,
            [card.memberCouponUuid]: rules,
          };
          commonUtilities.createToast(
            "error",
            blockedText || "優惠內容已更新，目前未達使用條件"
          );
          return;
        }
        // 需選規格：交給結帳頁開商品詳情，加入後才套用
        if (exchange.status === "ready" && exchange.needsDetail) {
          this.$emit("exchange", {
            selectedCoupon,
            menuItem: exchange.menuItem,
            quantity: exchange.quantity,
          });
          this.close();
          return;
        }
        const removedNames = getCouponExchangeItemNames(
          this.getCart.filter(
            (item) =>
              isCouponExchangeItem(item) &&
              item.couponExchange.memberCouponUuid !== card.memberCouponUuid
          )
        );
        if (exchange.status === "ready") {
          this.$store.commit("cart/applyCouponWithExchange", {
            selectedCoupon,
            orderItem: buildCouponExchangeOrderItem({
              menuItem: exchange.menuItem,
              quantity: exchange.quantity,
              specifications:
                this.$store.getters["spreadsheet/specifications"] || [],
              memberCouponUuid: card.memberCouponUuid,
            }),
          });
        } else {
          this.$store.commit("cart/setSelectedCoupon", selectedCoupon);
        }
        if (removedNames) {
          commonUtilities.createToast(
            "info",
            `已移除兌換商品「${removedNames}」`
          );
        }
        if (exchange.status === "ready") {
          commonUtilities.createToast(
            "success",
            `已加入兌換商品「${getFrontEndName(exchange.menuItem.name)}」×${
              exchange.quantity
            }`
          );
        }
        this.close();
      } catch (error) {
        commonUtilities.createToast(
          "error",
          error?.code && error.code !== "COUPON_INVALID_CONTRACT"
            ? getCouponCheckoutErrorMessage(error)
            : "優惠券確認失敗，請稍後再試"
        );
        this.load();
      } finally {
        this.validatingUuid = "";
      }
    },
    clearCoupon() {
      const removedNames = getCouponExchangeItemNames(
        this.getCart.filter(isCouponExchangeItem)
      );
      this.$store.commit("cart/setSelectedCoupon", null);
      if (removedNames) {
        commonUtilities.createToast(
          "info",
          `已移除兌換商品「${removedNames}」`
        );
      }
      this.close();
    },
    close() {
      this.isPickerActive = false;
      clearTimeout(this.validityTimer);
      this.$emit("close");
    },
  },
};
</script>
<style lang="scss" scoped>
.coupon_picker_popup {
  position: fixed;
  inset: 0;
  z-index: 999;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.coupon_picker_sheet {
  width: 100%;
  max-width: 600px;
  max-height: 86vh;
  background-color: #fff;
  border-radius: 16px 16px 0 0;
  padding: 18px 16px calc(env(safe-area-inset-bottom) + 16px);
  display: flex;
  flex-direction: column;
}

.coupon_picker_head {
  align-items: flex-start;
  margin-bottom: 10px;
}

.coupon_picker_title {
  color: #fd5001;
  font-size: 19px;
  font-weight: 600;
}

.coupon_picker_sub {
  font-size: 13px;
  color: #888;
}

.coupon_picker_close {
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: #999;
}

.coupon_picker_list {
  flex: 1;
  overflow-y: auto;
}

.coupon_picker_state {
  padding: 40px 10px;
  text-align: center;
  color: #888;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.coupon_picker_retry {
  margin-top: 12px;
  border: 1px solid #fd5001;
  color: #fd5001;
  background-color: #fff;
  border-radius: 8px;
  padding: 6px 18px;
}

.coupon_picker_card {
  width: 100%;
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 10px;
  padding: 12px 14px;
  border: 1.5px solid #eee;
  border-radius: 10px;
  background-color: #fff;
  color: #333;
  text-align: left;

  &.selected {
    border-color: #fd5001;
    background-color: #fff8f3;
  }

  &.disabled {
    background-color: #f7f7f7;
    color: #999;
  }
}

.coupon_picker_select {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
}

.coupon_picker_actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.coupon_picker_detail {
  border: 1px solid #fd5001;
  background-color: #fff;
  color: #fd5001;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;
}

.coupon_picker_select:disabled,
.coupon_picker_detail:disabled {
  cursor: default;
  opacity: 0.5;
}

.coupon_picker_radio {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 3px;
  border: 2px solid #ccc;
  border-radius: 50%;

  .selected & {
    border: 6px solid #fd5001;
  }

  .disabled & {
    border-color: #ddd;
    background-color: #eee;
  }
}

.coupon_picker_body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.coupon_picker_card_title {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.coupon_picker_card_sub {
  margin-top: 2px;
  font-size: 13px;
  color: #888;

  .disabled & {
    color: #b45309;
  }
}

.coupon_picker_exchange {
  color: #fd5001;
}

.coupon_picker_right {
  white-space: nowrap;
  font-weight: 600;
  color: #fd5001;

  .disabled & {
    font-size: 14px;
    font-weight: 500;
    color: #999;
  }
}

.coupon_picker_foot {
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;

  button {
    flex: 1;
    padding: 10px 0;
    border-radius: 8px;
    font-weight: 600;
  }
}

.coupon_picker_none {
  border: 1px solid #ccc;
  background-color: #fff;
  color: #555;
}

.coupon_picker_done {
  border: none;
  background-color: #facb06;
}
</style>
